import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { actions as peopleFollowActions } from 'Slice/PeopleFollowSlice';
import { selectFollowFailed, selectFollowLoading } from 'Selector/PeopleFollowSelector';
import { selectData } from 'Selector/CertificationSelector';
import { actions as certificationActions } from 'Slice/CertificationSlice';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { UpdatePersonProfileFollow } from 'Types/RequestBodies';
import { SEARCH } from 'Utils/Constants';
import { getCurrentTime, isOnboardingRoute, valueToObject } from 'Utils/UtilityFunctions';

type UsePersonFollowParams = {
  personId: number;
  personName: string;
  personality?: string;
  personAdded?: boolean;
  follow?: boolean;
  isSearchPage?: boolean;
  isLivePerson?: boolean;
};

export const usePersonFollow = ({
  personId,
  personName,
  personality = 'EXECUTIVE',
  personAdded = false,
  follow = true,
  isSearchPage = false,
  isLivePerson = false,
}: UsePersonFollowParams) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const followFailed = useSelector(selectFollowFailed);
  const followLoading = useSelector(selectFollowLoading);
  const certificationData = useSelector(selectData);
  const [disableButton, setDisableButton] = useState(false);
  const host = window.location.href;
  const startTime = getCurrentTime();
  const isOnboarding = isOnboardingRoute();

  useEffect(() => {
    setDisableButton(false);
  }, [followFailed, followLoading]);

  const handleFollowClick = useCallback((e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();

    if (!personAdded) {
      setDisableButton(true);
      const obj: UpdatePersonProfileFollow = {
        person_data: [{
          type: personality,
          query: personName,
          entity_id: personId,
        }],
      };
      const groupId = searchParams.get('groupId');
      if (pathname.includes(SEARCH) && groupId && parseInt(groupId, 10) > 0) {
        obj.group_id = parseInt(groupId, 10);
      }
      dispatch(peopleFollowActions.updateProfileFollow(obj));
      if (obj.person_data?.length && isOnboarding) {
        dispatch(flurryActions.callFlurryEvent(
          valueToObject('click_Follow_FollowPeople_Onboarding', startTime, {
            person_data: obj.person_data,
            host,
          }),
        ));
      }
    } else {
      setDisableButton(true);
      dispatch(peopleFollowActions.updateProfileFollow({
        feedids: [personId],
        type: personality,
      }));
      if (isOnboarding) {
        dispatch(flurryActions.callFlurryEvent(
          valueToObject('click_Unfollow_FollowPeople_Onboarding ', startTime, {
            feedids: [personId],
            type: personality,
            host,
          }),
        ));
      }
    }

    if (follow && !pathname.includes('onboarding')) {
      dispatch(certificationActions.certificationAction({
        type: 'person_follow',
        source: 'search',
        data: certificationData,
      }));
    }

    if (isSearchPage) {
      dispatch(flurryActions.callFlurryEvent(
        valueToObject(
          follow ? 'searchPage_person_follow_clicked' : 'searchPage_person_unfollow_clicked',
          0,
          {
            person_name: personName,
            person_id: personId,
            type: isLivePerson ? 'LIVESEARCH' : 'EXECUTIVE',
            host,
          },
        ),
      ));
    }
  }, [
    certificationData,
    dispatch,
    follow,
    host,
    isLivePerson,
    isOnboarding,
    isSearchPage,
    pathname,
    personAdded,
    personId,
    personName,
    personality,
    searchParams,
    startTime,
  ]);

  return { disableButton, handleFollowClick };
};

export default usePersonFollow;
